import { useEffect, useState } from 'react';

import { type RequestServiceOptions, utils } from '@atlaskit/util-service-support';

import { parseSiteARI } from '../../utils/ari';
import { type Site } from '../clients';
import { Products } from '../product-context/products';

import {
	type SearchConfig,
	type SearchConfigProviderProps,
	type Workspace,
} from './search-config-types';

enum ProductsFromARI {
	JIRA = 'jira',
	CONFLUENCE = 'confluence',
	TOWNSQUARE = 'townsquare', // this is same as Atlas
}

interface Resource {
	baseUrl: string;
	id: string; // ARI
	siteName: string;
}

interface SearchConfigAPIResponse {
	resources: Resource[];
}

const CONFIG_API_URL = '/configuration/v1';
export const AGGREGATOR_URL = '/gateway/api/xpsearch-aggregator';

const options: RequestServiceOptions = {
	path: CONFIG_API_URL,
	requestInit: {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
	},
};

const noAccessState = {
	jiraSites: [],
	confluenceSites: [],
	atlasWorkspaces: [],
};

const dedupeEntriesById = (arr: Workspace[]) => {
	return arr.filter(
		(initial, index, currentArray) =>
			currentArray.findIndex((matched) => matched.id === initial.id) === index,
	);
};

const dedupeEntriesByCloudId = (arr: Site[]) => {
	return arr.filter(
		(initial, index, currentArray) =>
			currentArray.findIndex((matched) => matched.cloudId === initial.cloudId) === index,
	);
};

export const parseResponse: (response: SearchConfigAPIResponse) => {
	jiraSites: import('../clients/common-types').Site[];
	confluenceSites: import('../clients/common-types').Site[];
	atlasWorkspaces: Workspace[];
} = (response: SearchConfigAPIResponse) => {
	const groupedProducts: SearchConfig = response.resources.reduce<SearchConfig>((acc, resource) => {
		const { baseUrl, id: ari, siteName } = resource;
		const { id, product } = parseSiteARI(ari);

		if (product.includes(ProductsFromARI.JIRA)) {
			acc.jiraSites &&
				acc.jiraSites.push({
					product: Products.jira,
					avatarUrl: '',
					cloudId: id,
					siteUrl: baseUrl,
					siteName,
				});
		}

		if (product === ProductsFromARI.CONFLUENCE) {
			acc.confluenceSites &&
				acc.confluenceSites.push({
					product: Products.confluence,
					avatarUrl: '',
					cloudId: id,
					siteUrl: baseUrl,
					siteName,
				});
		}

		if (product === ProductsFromARI.TOWNSQUARE) {
			acc.atlasWorkspaces &&
				acc.atlasWorkspaces.push({
					name: siteName,
					id,
					baseUrl,
				});
		}

		return acc;
	}, noAccessState);

	return {
		jiraSites: dedupeEntriesByCloudId(groupedProducts.jiraSites),
		confluenceSites: dedupeEntriesByCloudId(groupedProducts.confluenceSites),
		atlasWorkspaces: dedupeEntriesById(groupedProducts.atlasWorkspaces),
	};
};

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const useSearchConfigClient: (props: SearchConfigProviderProps) => SearchConfig | null = (
	props: SearchConfigProviderProps,
) => {
	const { aggregatorUrl = `${AGGREGATOR_URL}`, skip = false } = props;
	const [accessibleProducts, setAccessibleProducts] = useState<SearchConfig | null>(null);

	useEffect(() => {
		if (!skip && accessibleProducts === null) {
			utils
				.requestService<SearchConfigAPIResponse>({ url: aggregatorUrl }, options)
				.then((resp) => {
					setAccessibleProducts(parseResponse(resp));
				})
				.catch((_e) => {
					setAccessibleProducts(noAccessState);
				});
		}
	}, [accessibleProducts, setAccessibleProducts, aggregatorUrl, skip]);

	return accessibleProducts;
};
